import gql from 'graphql-tag';
/* eslint-disable @homeaway/graphql-fragments/fragment-files */

/**
 * Typically fragments will live in the component fragment file, but some fragments are used in multiple components or queries/mutations
 */

const commonFragments = {
    CheckoutInputs: {
        CheckoutExperienceModel: gql`
            fragment CheckoutInputsCheckoutExperienceModel on CheckoutExperienceModel {
                checkoutInputs {
                    arrivalDate
                    departureDate
                    unit
                    adults
                    children
                    withPets
                }
            }
        `,
    },
    ClientValidators: {
        CheckoutExperienceModel: gql`
            fragment ClientValidatorsCheckoutExperienceModel on CheckoutExperienceModel {
                clientValidators {
                    postalCodeValidators {
                        countryCode
                        postalCode
                    }
                }
            }
        `,
    },
    Disclosures: {
        CheckoutExperienceModel: gql`
            fragment DisclosuresCheckoutExperienceModel on CheckoutExperienceModel {
                disclosures {
                    type
                    label
                    url
                }
            }
        `,
    },
    Payload: {
        CheckoutExperienceModel: gql`
            fragment PayloadCheckoutExperienceModel on CheckoutExperienceModel {
                payload {
                    data
                    signature
                }
            }
        `,
    },
    Quote: {
        CheckoutExperienceModel: gql`
            fragment QuoteCheckoutExperienceModel on CheckoutExperienceModel {
                quote {
                    isPayLaterBooking
                    lineItems {
                        type
                        subType
                        label
                        amount
                        decimalAmount
                        note
                    }
                    serviceFee {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    refundableDeposit {
                        label
                        amount
                        decimalAmount
                        note
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    total {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    subTotal {
                        label
                        amount
                        decimalAmount
                        note
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    paymentRequests {
                        description
                        label
                        amount
                        decimalAmount
                        note
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    completedPaymentRequests {
                        paymentStatus
                    }
                    additionalInformation
                    estimatedTotal {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    currencyCode
                    currencySymbol
                    paymentPlans {
                        id
                        label
                        amount
                        description
                        supportPaymentInFull
                        decimalAmount
                        additionalPaymentRequests {
                            description
                            label
                            amount
                            decimalAmount
                            note
                            supportedPaymentTypes
                            dueDate
                        }
                        dueDate
                        installmentOptions {
                            numberOfInstallments
                            amount {
                                amount
                                currency
                                localized
                            }
                        }
                    }
                    quoteTotals {
                        total {
                            amount
                            currency
                            localized
                            estimated {
                                amount
                                currency
                                localized
                            }
                        }
                        totalTax {
                            amount
                            currency
                            localized
                            estimated {
                                amount
                                currency
                                localized
                            }
                        }
                        additionalTotals {
                            name
                            total {
                                amount
                                currency
                                localized
                                estimated {
                                    amount
                                    currency
                                    localized
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    // Used in olp-header and reservation-inputs
    ReservationInformation: {
        CheckoutExperienceModel: gql`
            fragment ReservationInformationCheckoutExperienceModel on CheckoutExperienceModel {
                reservationInformation {
                    arrivalDate
                    departureDate
                    currentPaymentNumber
                    pets
                    adults
                    childAges
                    reservationReferenceNumber
                }
            }
        `,
    },
    SiteSettings: {
        CheckoutExperienceModel: gql`
            fragment SiteSettingsCheckoutExperienceModel on CheckoutExperienceModel {
                siteSettings {
                    productNoticeForUSProducts
                }
            }
        `,
    },
    StayCollectedFeeQuote: {
        CheckoutExperienceModel: gql`
            fragment StayCollectedFeeQuoteCheckoutExperienceModel on CheckoutExperienceModel {
                stayCollectedFeeQuote {
                    lineItems {
                        type
                        subType
                        label
                        amount
                        decimalAmount
                        note
                    }
                    serviceFee {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    stayLineItems {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    stayCollectedFeeTotal {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    refundableDeposit {
                        label
                        amount
                        decimalAmount
                        note
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    total {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    subTotal {
                        label
                        amount
                        decimalAmount
                        note
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    paymentRequests {
                        description
                        label
                        amount
                        decimalAmount
                        note
                        dueDate
                        estimated {
                            amount
                            currency
                            localized
                        }
                    }
                    additionalInformation
                    estimatedTotal {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    currencyCode
                    refundableStayLineItems {
                        label
                        amount
                        decimalAmount
                        note
                    }
                    quoteTotals {
                        total {
                            amount
                            currency
                            localized
                            estimated {
                                amount
                                currency
                                localized
                            }
                        }
                        totalTax {
                            amount
                            currency
                            localized
                            estimated {
                                amount
                                currency
                                localized
                            }
                        }
                        additionalTotals {
                            name
                            total {
                                amount
                                currency
                                localized
                                estimated {
                                    amount
                                    currency
                                    localized
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    Texts: {
        CheckoutExperienceModel: gql`
            fragment TextsCheckoutExperienceModel on CheckoutExperienceModel {
                texts {
                    confirmationOfp
                    checkoutSubmitLabel
                    defaultPaymentInstructions
                    paymentDetailsDescription
                    paymentNoteDescription
                }
            }
        `,
    },
    TripInformation: {
        CheckoutExperienceModel: gql`
            fragment TripInformationCheckoutExperienceModel on CheckoutExperienceModel {
                tripInformation {
                    id
                }
            }
        `,
    },
    TravelerInformation: {
        CheckoutExperienceModel: gql`
            fragment TravelerInformationCheckoutExperienceModel on CheckoutExperienceModel {
                travelerInformation {
                    email
                    firstName
                    lastName
                    phone
                }
            }
        `,
    },
    coBccEligibility: {
        CheckoutExperienceModel: gql`
            fragment coBccEligibilityCheckoutExperienceModel on CheckoutExperienceModel {
                coBccEligibility {
                    eligible
                    ineligibility_reason_code
                }
            }
        `,
    },
    coBccConfigs: {
        CheckoutExperienceModel: gql`
            fragment coBccConfigsCheckoutExperienceModel on CheckoutExperienceModel {
                coBccConfigs {
                    jaylaToken
                    partnerMemberId
                }
            }
        `,
    },
};

export default commonFragments;
